/**
 * CRDT schema v2 incremental projection (LG-2849) — offset-preserving stream helpers.
 *
 * Absolute XmlText index space: text length, or 1 per embed (including kinds `projectStream` skips).
 * Live Yjs observe events are adapted by LG-2850 onto `ObserveDelta`.
 */

import { Language } from 'src/models/Language'

import { MARKER_KINDS } from './constants'
import { TimedParagraph } from './derived'
import { Marker, ParagraphChangeMarker, Qac, UtteranceChangeMarker } from './markers'
import { StreamDelta, projectStream } from './projection'
import { TimingsMap, joinTimings } from './timingsJoin'

export type OpaqueEmbed = { kind: string; [key: string]: unknown }

export type StreamEmbed = Marker | OpaqueEmbed

export type IndexedOp = { insert: string } | { insert: StreamEmbed }

export type IndexedStream = IndexedOp[]

export type ObserveDeltaOp =
    | { retain: number; attributes?: Record<string, unknown> }
    | { insert: string | StreamEmbed }
    | { delete: number }

export type ObserveDelta = ObserveDeltaOp[]

export type ParagraphRange = { id: string; start: number; length: number }

export type ProjectionIndex = {
    stream: IndexedStream
    ranges: ParagraphRange[]
    paragraphs: TimedParagraph[]
}

export type ApplyResult = {
    index: ProjectionIndex
    dirtyIds: string[]
    addedIds: string[]
    removedIds: string[]
    usedFullParse: boolean
    localizationFailed: boolean
}

export type ApplyObserveDeltaResult = { ok: true; stream: IndexedStream } | { ok: false }

export type EmbedFieldPatch = {
    /** Id stored in the projected stream. For a rename this is the previous paragraph id. */
    markerId: string
    fields: Partial<{ speakerId: string; qac: Qac; final: boolean }>
    /**
     * New `paragraph_change` id. Empty string means the map write was not a usable id and the
     * patch must localize-fail. Absent when `id` did not change.
     */
    renameToId?: string
}

export const indexedOpLength = (op: IndexedOp): number => (typeof op.insert === 'string' ? op.insert.length : 1)

const KNOWN_MARKER_KINDS = new Set<string>(Object.values(MARKER_KINDS))

const isKnownMarker = (embed: StreamEmbed): embed is Marker => KNOWN_MARKER_KINDS.has(embed.kind)

const isParagraphChangeOp = (op: IndexedOp): op is { insert: ParagraphChangeMarker } =>
    typeof op.insert !== 'string' && op.insert.kind === MARKER_KINDS.paragraphChange

const isUtteranceChangeOp = (op: IndexedOp): op is { insert: UtteranceChangeMarker } =>
    typeof op.insert !== 'string' && op.insert.kind === MARKER_KINDS.utteranceChange

const cloneOp = (op: IndexedOp): IndexedOp => (typeof op.insert === 'string' ? { insert: op.insert } : { insert: { ...op.insert } })

const concatAdjacentText = (ops: IndexedOp[]): IndexedStream => {
    const out: IndexedOp[] = []

    for (const op of ops) {
        if (typeof op.insert === 'string' && op.insert.length === 0) {
            continue
        }

        const last = out[out.length - 1]
        if (last !== undefined && typeof last.insert === 'string' && typeof op.insert === 'string') {
            out[out.length - 1] = { insert: last.insert + op.insert }
        } else {
            out.push(op)
        }
    }

    return out
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const isStreamEmbed = (value: unknown): value is StreamEmbed => isRecord(value) && typeof value.kind === 'string'

/**
 * Runtime-validate one observe op. Live Yjs `toDelta()` output is untrusted here (LG-2850), so a null,
 * primitive, multi-action, or malformed-embed entry must localize-fail rather than throw when the
 * `in` operator or `embed.kind` is dereferenced. `retain`/`delete` must be finite non-negative
 * integers: `NaN` would slip past the `> length` bounds check as a silent no-op and a fraction would
 * desync cursor arithmetic from `splitAt`/`String.slice` (which floor), mis-mapping later ops.
 */
const isObserveStep = (step: unknown): step is ObserveDeltaOp => {
    if (!isRecord(step)) {
        return false
    }

    const actions = Number('retain' in step) + Number('insert' in step) + Number('delete' in step)
    if (actions !== 1) {
        return false
    }

    if ('insert' in step) {
        return typeof step.insert === 'string' || isStreamEmbed(step.insert)
    }
    if ('retain' in step) {
        return Number.isInteger(step.retain) && (step.retain as number) >= 0
    }

    return Number.isInteger(step.delete) && (step.delete as number) >= 0
}

/** Split so `pos` is at the start of an op (or EOF). Mutates `ops`. Null if `pos` is out of range. */
const splitAt = (ops: IndexedOp[], pos: number): number | null => {
    if (pos < 0) {
        return null
    }

    let remaining = pos
    for (let i = 0; i < ops.length; i++) {
        if (remaining === 0) {
            return i
        }

        const len = indexedOpLength(ops[i])
        if (remaining < len) {
            const { insert } = ops[i]
            if (typeof insert !== 'string') {
                return null
            }

            ops.splice(i, 1, { insert: insert.slice(0, remaining) }, { insert: insert.slice(remaining) })

            return i + 1
        }

        remaining -= len
    }

    return remaining === 0 ? ops.length : null
}

export const applyObserveDelta = (stream: IndexedStream, delta: ObserveDelta): ApplyObserveDeltaResult => {
    const ops = stream.slice()
    let cursor = 0
    let length = ops.reduce((sum, op) => sum + indexedOpLength(op), 0)

    for (const step of delta) {
        if (!isObserveStep(step)) {
            return { ok: false }
        }

        if ('retain' in step) {
            if (cursor + step.retain > length) {
                return { ok: false }
            }
            cursor += step.retain
            continue
        }

        if ('insert' in step) {
            const inserted: IndexedOp = typeof step.insert === 'string' ? { insert: step.insert } : { insert: { ...step.insert } }
            const index = splitAt(ops, cursor)
            if (index === null) {
                return { ok: false }
            }
            ops.splice(index, 0, inserted)
            const added = indexedOpLength(inserted)
            cursor += added
            length += added
            continue
        }

        if (cursor + step.delete > length) {
            return { ok: false }
        }

        const start = splitAt(ops, cursor)
        const end = splitAt(ops, cursor + step.delete)
        if (start === null || end === null) {
            return { ok: false }
        }
        ops.splice(start, end - start)
        length -= step.delete
    }

    return { ok: true, stream: concatAdjacentText(ops) }
}

export const toProjectableDelta = (stream: IndexedStream): StreamDelta => {
    const projectable: StreamDelta = []

    for (const op of stream) {
        if (typeof op.insert === 'string') {
            const last = projectable[projectable.length - 1]
            if (last !== undefined && typeof last.insert === 'string') {
                projectable[projectable.length - 1] = { insert: last.insert + op.insert }
            } else if (op.insert.length > 0) {
                projectable.push({ insert: op.insert })
            }
            continue
        }
        if (isKnownMarker(op.insert)) {
            projectable.push({ insert: op.insert })
        }
    }

    return projectable
}

export const rangesFromStream = (stream: IndexedStream): ParagraphRange[] => {
    const ranges: ParagraphRange[] = []
    let offset = 0
    let current: { id: string; start: number } | null = null

    const closeCurrent = () => {
        if (current !== null) {
            ranges.push({ id: current.id, start: current.start, length: offset - current.start })
        }
    }

    for (const op of stream) {
        if (isParagraphChangeOp(op)) {
            closeCurrent()
            current = { id: op.insert.id, start: offset }
        }
        offset += indexedOpLength(op)
    }

    closeCurrent()

    return ranges
}

const streamLength = (stream: IndexedStream): number => stream.reduce((sum, op) => sum + indexedOpLength(op), 0)

const isSkippedEmbed = (embed: StreamEmbed): boolean =>
    embed.kind !== MARKER_KINDS.paragraphChange && embed.kind !== MARKER_KINDS.utteranceChange

const paragraphIdForInsert = (pos: number, ranges: ParagraphRange[]): string | null => {
    if (ranges.length === 0) {
        return null
    }
    if (pos === 0) {
        return ranges[0].id
    }

    for (const range of ranges) {
        const end = range.start + range.length
        if (pos > range.start && pos <= end) {
            return range.id
        }
    }

    return null
}

const paragraphIdsOverlapping = (start: number, end: number, ranges: ParagraphRange[]): string[] => {
    const ids: string[] = []

    for (const range of ranges) {
        const rangeEnd = range.start + range.length
        if (range.start < end && start < rangeEnd) {
            ids.push(range.id)
        }
    }

    return ids
}

const sliceStream = (stream: IndexedStream, start: number, end: number): IndexedStream | null => {
    if (start < 0 || end < start) {
        return null
    }

    const out: IndexedOp[] = []
    let offset = 0

    for (const op of stream) {
        const len = indexedOpLength(op)
        const opEnd = offset + len

        if (opEnd <= start) {
            offset = opEnd
            continue
        }
        if (offset >= end) {
            break
        }

        const from = Math.max(start, offset) - offset
        const to = Math.min(end, opEnd) - offset

        if (typeof op.insert === 'string') {
            const piece = op.insert.slice(from, to)
            if (piece.length > 0) {
                out.push({ insert: piece })
            }
        } else {
            if (from !== 0 || to !== 1) {
                return null
            }
            out.push(cloneOp(op))
        }

        offset = opEnd
    }

    if (end > offset) {
        return null
    }

    return concatAdjacentText(out)
}

/** True when [start, end) is only skipped embeds. Null if the range is out of bounds or mid-embed. */
const rangeIsSkippedEmbedsOnly = (stream: IndexedStream, start: number, end: number): boolean | null => {
    if (start < 0 || end < start) {
        return null
    }

    let offset = 0

    for (const op of stream) {
        const len = indexedOpLength(op)
        const opEnd = offset + len

        if (opEnd <= start) {
            offset = opEnd
            continue
        }
        if (offset >= end) {
            break
        }

        const from = Math.max(start, offset) - offset
        const to = Math.min(end, opEnd) - offset

        if (typeof op.insert === 'string') {
            if (to > from) {
                return false
            }
        } else {
            if (from !== 0 || to !== 1) {
                return null
            }
            if (!isSkippedEmbed(op.insert)) {
                return false
            }
        }

        offset = opEnd
    }

    if (end > offset) {
        return null
    }

    return true
}

const dirtyIdsFromDelta = (delta: ObserveDelta, ranges: ParagraphRange[], oldLength: number, stream: IndexedStream): string[] | null => {
    const dirty: string[] = []
    const seen = new Set<string>()
    let cursor = 0

    const add = (id: string | null) => {
        if (id !== null && !seen.has(id)) {
            seen.add(id)
            dirty.push(id)
        }
    }

    for (const step of delta) {
        if (!isObserveStep(step)) {
            return null
        }

        if ('retain' in step) {
            if (cursor + step.retain > oldLength) {
                return null
            }
            cursor += step.retain
            continue
        }

        if ('insert' in step) {
            const affectsRuns = typeof step.insert === 'string' ? step.insert.length > 0 : !isSkippedEmbed(step.insert)
            if (affectsRuns) {
                const id = paragraphIdForInsert(cursor, ranges)
                if (id === null) {
                    return null
                }
                add(id)
            }
            continue
        }

        if (step.delete === 0) {
            continue
        }
        if (cursor + step.delete > oldLength) {
            return null
        }
        const deleteIsSkippedOnly = rangeIsSkippedEmbedsOnly(stream, cursor, cursor + step.delete)
        if (deleteIsSkippedOnly === null) {
            return null
        }
        if (!deleteIsSkippedOnly) {
            const overlapping = paragraphIdsOverlapping(cursor, cursor + step.delete, ranges)
            if (overlapping.length === 0) {
                return null
            }
            for (const id of overlapping) {
                add(id)
            }
        }
        cursor += step.delete
    }

    return dirty
}

const contiguousDirtySpans = (orderedIds: string[], dirtyIds: string[]): string[][] => {
    const dirty = new Set(dirtyIds)
    const spans: string[][] = []
    let current: string[] = []

    for (const id of orderedIds) {
        if (dirty.has(id)) {
            current.push(id)
        } else if (current.length > 0) {
            spans.push(current)
            current = []
        }
    }
    if (current.length > 0) {
        spans.push(current)
    }

    return spans
}

/**
 * The utterance active when a window at `offset` begins: the last `utterance_change` marker strictly
 * before it in the stream. Derived from markers, not emitted runs — a trailing `utterance_change` that
 * bounds a zero-length segment emits no run but still carries into the next paragraph, so seeding from
 * the previous paragraph's last run would drop it and mis-tag the window (wrong id/final/timings).
 *
 * Used by the general `reprojectDirtySpans` path (arbitrary spans, char offset). The tail fast path
 * uses `activeUtteranceBeforeOp` instead — same result, but a backward op-index scan so it stays
 * O(window) rather than scanning from offset 0.
 */
const activeUtteranceBefore = (stream: IndexedStream, offset: number): UtteranceChangeMarker | null => {
    let cursor = 0
    let active: UtteranceChangeMarker | null = null

    for (const op of stream) {
        if (cursor >= offset) {
            break
        }
        if (isUtteranceChangeOp(op)) {
            active = op.insert
        }
        cursor += indexedOpLength(op)
    }

    return active
}

const seedFromPrefix = (paragraphs: TimedParagraph[]): Map<string, number> => {
    const seed = new Map<string, number>()

    for (const paragraph of paragraphs) {
        for (const run of paragraph.runs) {
            if (run.utteranceId === null) {
                continue
            }
            seed.set(run.utteranceId, (seed.get(run.utteranceId) ?? 0) + run.words.length)
        }
    }

    return seed
}

const emptyApply = (index: ProjectionIndex, localizationFailed: boolean): ApplyResult => ({
    index,
    dirtyIds: [],
    addedIds: [],
    removedIds: [],
    usedFullParse: false,
    localizationFailed,
})

const localizedApply = (
    index: ProjectionIndex,
    dirtyIds: string[] = [],
    addedIds: string[] = [],
    removedIds: string[] = [],
): ApplyResult => ({
    index,
    dirtyIds,
    addedIds,
    removedIds,
    usedFullParse: false,
    localizationFailed: false,
})

const addedAndRemovedIds = (previous: ParagraphRange[], next: ParagraphRange[]): { addedIds: string[]; removedIds: string[] } => {
    const previousIds = new Set(previous.map((range) => range.id))
    const nextIds = new Set(next.map((range) => range.id))

    return {
        addedIds: next.map((range) => range.id).filter((id) => !previousIds.has(id)),
        removedIds: previous.map((range) => range.id).filter((id) => !nextIds.has(id)),
    }
}

const addUnique = (ids: string[], seen: Set<string>, id: string | undefined) => {
    if (id !== undefined && !seen.has(id)) {
        seen.add(id)
        ids.push(id)
    }
}

const expandSplitMergeNeighbors = (
    intersecting: string[],
    previous: ParagraphRange[],
    next: ParagraphRange[],
    addedIds: string[],
    removedIds: string[],
): string[] => {
    const seen = new Set(intersecting)
    const dirty = [...intersecting]

    for (const id of addedIds) {
        addUnique(dirty, seen, id)
        const index = next.findIndex((range) => range.id === id)
        addUnique(dirty, seen, next[index - 1]?.id)
        addUnique(dirty, seen, next[index + 1]?.id)
    }

    for (const id of removedIds) {
        const index = previous.findIndex((range) => range.id === id)
        addUnique(dirty, seen, previous[index - 1]?.id)
        addUnique(dirty, seen, previous[index + 1]?.id)
    }

    return dirty
}

const expandUtteranceSiblings = (dirtyIds: string[], paragraphs: TimedParagraph[]): string[] => {
    const seen = new Set(dirtyIds)
    const utteranceIds = new Set<string>()

    for (const paragraph of paragraphs) {
        if (!seen.has(paragraph.id)) {
            continue
        }
        for (const run of paragraph.runs) {
            if (run.utteranceId !== null) {
                utteranceIds.add(run.utteranceId)
            }
        }
    }

    const dirty = [...dirtyIds]
    for (const paragraph of paragraphs) {
        if (seen.has(paragraph.id)) {
            continue
        }
        if (paragraph.runs.some((run) => run.utteranceId !== null && utteranceIds.has(run.utteranceId))) {
            addUnique(dirty, seen, paragraph.id)
        }
    }

    return dirty
}

const orderDirtyIds = (dirtyIds: string[], ranges: ParagraphRange[]): string[] => {
    const dirty = new Set(dirtyIds)

    return ranges.map((range) => range.id).filter((id) => dirty.has(id))
}

const reprojectDirtySpans = (
    previous: ProjectionIndex,
    stream: IndexedStream,
    ranges: ParagraphRange[],
    dirtyIds: string[],
    timings: TimingsMap,
    language: Language,
): TimedParagraph[] | null => {
    if (dirtyIds.length === 0) {
        return [...previous.paragraphs]
    }

    const byId = new Map(previous.paragraphs.map((paragraph) => [paragraph.id, paragraph]))
    const replacements = new Map<string, TimedParagraph>()
    const paragraphFor = (id: string): TimedParagraph | undefined => replacements.get(id) ?? byId.get(id)

    for (const span of contiguousDirtySpans(
        ranges.map((range) => range.id),
        dirtyIds,
    )) {
        const first = ranges.find((range) => range.id === span[0])
        const last = ranges.find((range) => range.id === span[span.length - 1])
        if (first === undefined || last === undefined) {
            return null
        }

        const slice = sliceStream(stream, first.start, last.start + last.length)
        if (slice === null) {
            return null
        }

        const spanStart = ranges.findIndex((range) => range.id === span[0])
        const prefix: TimedParagraph[] = []
        for (const range of ranges.slice(0, spanStart)) {
            const paragraph = paragraphFor(range.id)
            if (paragraph === undefined) {
                return null
            }
            prefix.push(paragraph)
        }

        const projected = joinTimings(
            projectStream(toProjectableDelta(slice), activeUtteranceBefore(stream, first.start)),
            timings,
            language,
            seedFromPrefix(prefix),
        )

        if (projected.length !== span.length || projected.some((paragraph, i) => paragraph.id !== span[i])) {
            return null
        }

        for (const paragraph of projected) {
            replacements.set(paragraph.id, paragraph)
        }
    }

    const paragraphs: TimedParagraph[] = []
    for (const range of ranges) {
        const paragraph = paragraphFor(range.id)
        if (paragraph === undefined) {
            return null
        }
        paragraphs.push(paragraph)
    }

    return paragraphs
}

/**
 * Apply a field patch to the located marker. Returns null when the patch is incompatible with the
 * marker type (e.g. `final` on a `paragraph_change`, `speakerId`/`qac` on an `utterance_change`, or a
 * patch with no field the marker accepts) so the caller localize-fails instead of silently dropping
 * the update while reporting success.
 */
const patchEmbedOp = (op: IndexedOp, fields: EmbedFieldPatch['fields']): IndexedOp | null => {
    if (isParagraphChangeOp(op)) {
        const setsSpeaker = fields.speakerId !== undefined
        const setsQac = 'qac' in fields
        if (fields.final !== undefined || (!setsSpeaker && !setsQac)) {
            return null
        }

        const next: ParagraphChangeMarker = { ...op.insert }
        if (setsSpeaker) {
            next.speakerId = fields.speakerId as string
        }
        if (setsQac) {
            if (fields.qac === undefined) {
                delete next.qac
            } else {
                next.qac = fields.qac
            }
        }

        return { insert: next }
    }

    if (isUtteranceChangeOp(op)) {
        if (fields.speakerId !== undefined || 'qac' in fields || fields.final === undefined) {
            return null
        }

        return { insert: { ...op.insert, final: fields.final } }
    }

    return null
}

/**
 * Locate `markerId` and, when `renameToId` is set, whether another paragraph already uses it.
 * One pass. A rename only matches `paragraph_change`; utterance lookup stays on the field-patch path.
 */
const findPatchableOpIndex = (stream: IndexedStream, markerId: string, renameToId?: string): { index: number; collides: boolean } => {
    let index = -1
    let collides = false

    for (let i = 0; i < stream.length; i++) {
        const op = stream[i]
        if (isParagraphChangeOp(op)) {
            if (index < 0 && op.insert.id === markerId) {
                index = i
                if (renameToId === undefined) {
                    return { index, collides: false }
                }
            } else if (renameToId !== undefined && op.insert.id === renameToId) {
                collides = true
            }
            continue
        }

        if (renameToId === undefined && index < 0 && isUtteranceChangeOp(op) && op.insert.id === markerId) {
            return { index: i, collides: false }
        }
    }

    return { index, collides }
}

/**
 * The utterance active immediately before op `opIndex`, found by scanning backward to the nearest
 * `utterance_change` (bounded by utterance size in practice). O(window), unlike `activeUtteranceBefore`
 * which scans from offset 0.
 */
const activeUtteranceBeforeOp = (stream: IndexedStream, opIndex: number): UtteranceChangeMarker | null => {
    for (let i = opIndex - 1; i >= 0; i--) {
        const op = stream[i]
        if (isUtteranceChangeOp(op)) {
            return op.insert
        }
    }

    return null
}

/**
 * Fast path for edits confined to the **last paragraph's region**: ASR interim suffix inserts, an
 * appended/split `paragraph_change` at the tail, or a replaced `utterance_change` near the end — the
 * ~entire read-only ASR workload. Reprojects only the last-paragraph slice in O(window) and reuses
 * every prior paragraph / range / op by reference, avoiding the general path's O(total words)
 * `seedFromPrefix` / slice / offset-0 scans.
 *
 * Returns `null` when the edit is not a pure tail edit (touches the last `paragraph_change` marker or
 * anything before it, is malformed, or goes out of bounds) — the caller falls back to the general
 * path. Output is identical to a full `syncProjection` of the resulting stream.
 */
export const applyTailTextDelta = (
    index: ProjectionIndex,
    delta: ObserveDelta,
    timings: TimingsMap,
    language: Language,
): ApplyResult | null => {
    const { ranges, stream, paragraphs } = index
    if (ranges.length === 0) {
        return null
    }

    const lastRange = ranges[ranges.length - 1]
    const totalLength = lastRange.start + lastRange.length

    let cursor = 0
    let firstEditPos = -1
    for (const step of delta) {
        if (!isObserveStep(step)) {
            return null
        }
        if ('retain' in step) {
            cursor += step.retain
            continue
        }
        firstEditPos = cursor
        break
    }

    // The edit must land strictly after the last paragraph_change marker (offset lastRange.start) and
    // within the stream — otherwise it can affect a prior paragraph and belongs on the general path.
    if (firstEditPos < lastRange.start + 1 || firstEditPos > totalLength) {
        return null
    }

    let lastOpStart = -1
    for (let i = stream.length - 1; i >= 0; i--) {
        if (isParagraphChangeOp(stream[i])) {
            lastOpStart = i
            break
        }
    }
    if (lastOpStart < 0) {
        return null
    }

    const lastSlice = stream.slice(lastOpStart)
    const localDelta: ObserveDelta = [{ retain: firstEditPos - lastRange.start }]
    let seenFirstEdit = false
    for (const step of delta) {
        if (!seenFirstEdit && 'retain' in step) {
            continue
        }
        seenFirstEdit = true
        localDelta.push(step)
    }

    const applied = applyObserveDelta(lastSlice, localDelta)
    if (!applied.ok) {
        return null
    }

    const newLastSlice = applied.stream
    const head = newLastSlice[0]
    if (head === undefined || !isParagraphChangeOp(head) || head.insert.id !== lastRange.id) {
        return null
    }

    const initialUtterance = activeUtteranceBeforeOp(stream, lastOpStart)
    const seed = new Map<string, number>()
    if (initialUtterance !== null) {
        let count = 0
        for (let i = paragraphs.length - 2; i >= 0; i--) {
            let words = 0
            let referencesUtterance = false
            for (const run of paragraphs[i].runs) {
                if (run.utteranceId === initialUtterance.id) {
                    referencesUtterance = true
                    words += run.words.length
                }
            }
            if (!referencesUtterance) {
                break
            }
            count += words
        }
        if (count > 0) {
            seed.set(initialUtterance.id, count)
        }
    }

    const projected = joinTimings(projectStream(toProjectableDelta(newLastSlice), initialUtterance), timings, language, seed)
    if (projected.length === 0 || projected[0].id !== lastRange.id) {
        return null
    }

    const newLastRanges = rangesFromStream(newLastSlice).map((range) => ({ ...range, start: range.start + lastRange.start }))

    return {
        index: {
            stream: stream.slice(0, lastOpStart).concat(newLastSlice),
            ranges: ranges.slice(0, -1).concat(newLastRanges),
            paragraphs: paragraphs.slice(0, -1).concat(projected),
        },
        // Mirror the general path's contract: every reprojected paragraph is dirty, and the
        // newly-introduced ones (beyond the original last paragraph) are also listed as added.
        dirtyIds: projected.map((paragraph) => paragraph.id),
        addedIds: projected.slice(1).map((paragraph) => paragraph.id),
        removedIds: [],
        usedFullParse: false,
        localizationFailed: false,
    }
}

export const applyTextDelta = (index: ProjectionIndex, delta: ObserveDelta, timings: TimingsMap, language: Language): ApplyResult => {
    const fastResult = applyTailTextDelta(index, delta, timings, language)
    if (fastResult !== null) {
        return fastResult
    }

    const applied = applyObserveDelta(index.stream, delta)
    if (!applied.ok) {
        return emptyApply(index, true)
    }

    const intersecting = dirtyIdsFromDelta(delta, index.ranges, streamLength(index.stream), index.stream)
    if (intersecting === null) {
        return { ...syncProjection(applied.stream, timings, language) }
    }

    const ranges = rangesFromStream(applied.stream)
    const { addedIds, removedIds } = addedAndRemovedIds(index.ranges, ranges)
    const dirtyIds = orderDirtyIds(
        expandUtteranceSiblings(expandSplitMergeNeighbors(intersecting, index.ranges, ranges, addedIds, removedIds), index.paragraphs),
        ranges,
    )

    if (dirtyIds.length === 0 && addedIds.length === 0 && removedIds.length === 0) {
        return localizedApply({ stream: applied.stream, ranges, paragraphs: [...index.paragraphs] })
    }

    if (dirtyIds.length === 0) {
        return { ...syncProjection(applied.stream, timings, language) }
    }

    const paragraphs = reprojectDirtySpans(index, applied.stream, ranges, dirtyIds, timings, language)
    if (paragraphs === null) {
        return { ...syncProjection(applied.stream, timings, language) }
    }

    return localizedApply({ stream: applied.stream, ranges, paragraphs }, dirtyIds, addedIds, removedIds)
}

const hasEmbedFields = (fields: EmbedFieldPatch['fields']): boolean =>
    fields.speakerId !== undefined || 'qac' in fields || fields.final !== undefined

const renameParagraphOp = (op: IndexedOp, fields: EmbedFieldPatch['fields'], renameToId: string): IndexedOp | null => {
    if (!isParagraphChangeOp(op)) {
        return null
    }

    const patched = hasEmbedFields(fields) ? patchEmbedOp(op, fields) : { insert: { ...op.insert } }
    if (patched === null || !isParagraphChangeOp(patched)) {
        return null
    }

    return { insert: { ...patched.insert, id: renameToId } }
}

const writePatchedOp = (index: ProjectionIndex, found: number, patched: IndexedOp): { stream: IndexedStream; ranges: ParagraphRange[] } => {
    const stream = index.stream.map(cloneOp)
    stream[found] = patched

    return { stream, ranges: rangesFromStream(stream) }
}

export const applyEmbedPatch = (index: ProjectionIndex, patch: EmbedFieldPatch, timings: TimingsMap, language: Language): ApplyResult => {
    const nextId = patch.renameToId
    if (nextId === '') {
        return emptyApply(index, true)
    }

    if (nextId !== undefined && nextId !== patch.markerId) {
        const located = findPatchableOpIndex(index.stream, patch.markerId, nextId)
        if (located.index < 0 || located.collides) {
            return emptyApply(index, true)
        }

        const renamed = renameParagraphOp(index.stream[located.index], patch.fields, nextId)
        if (renamed === null) {
            return emptyApply(index, true)
        }

        const { stream, ranges } = writePatchedOp(index, located.index, renamed)
        const paragraphs = reprojectDirtySpans(index, stream, ranges, [nextId], timings, language)
        if (paragraphs === null) {
            return { ...syncProjection(stream, timings, language) }
        }

        const { addedIds, removedIds } = addedAndRemovedIds(index.ranges, ranges)

        return localizedApply({ stream, ranges, paragraphs }, [], addedIds, removedIds)
    }

    const located = findPatchableOpIndex(index.stream, patch.markerId)
    if (located.index < 0) {
        return emptyApply(index, true)
    }

    const op = index.stream[located.index]
    const patched = patchEmbedOp(op, patch.fields)
    if (patched === null) {
        return emptyApply(index, true)
    }

    const { stream, ranges } = writePatchedOp(index, located.index, patched)
    const dirtyIds = isParagraphChangeOp(op)
        ? orderDirtyIds([op.insert.id], ranges)
        : orderDirtyIds(
              index.paragraphs
                  .filter((paragraph) => paragraph.runs.some((run) => run.utteranceId === patch.markerId))
                  .map((paragraph) => paragraph.id),
              ranges,
          )

    const paragraphs = reprojectDirtySpans(index, stream, ranges, dirtyIds, timings, language)
    if (paragraphs === null) {
        return { ...syncProjection(stream, timings, language) }
    }

    return localizedApply({ stream, ranges, paragraphs }, dirtyIds)
}

export const rejoinUtterances = (
    index: ProjectionIndex,
    utteranceIds: ReadonlySet<string>,
    timings: TimingsMap,
    language: Language,
): ApplyResult => {
    const dirtyIds = orderDirtyIds(
        index.paragraphs
            .filter((paragraph) => paragraph.runs.some((run) => run.utteranceId !== null && utteranceIds.has(run.utteranceId)))
            .map((paragraph) => paragraph.id),
        index.ranges,
    )

    if (dirtyIds.length === 0) {
        return localizedApply({ stream: index.stream, ranges: index.ranges, paragraphs: [...index.paragraphs] })
    }

    const paragraphs = reprojectDirtySpans(index, index.stream, index.ranges, dirtyIds, timings, language)
    if (paragraphs === null) {
        return { ...syncProjection(index.stream, timings, language) }
    }

    return localizedApply({ stream: index.stream, ranges: index.ranges, paragraphs }, dirtyIds)
}

export const syncProjection = (stream: IndexedStream, timings: TimingsMap, language: Language): ApplyResult => {
    const copied = stream.map(cloneOp)
    const paragraphs = joinTimings(projectStream(toProjectableDelta(copied)), timings, language)

    return {
        index: {
            stream: copied,
            ranges: rangesFromStream(copied),
            paragraphs,
        },
        dirtyIds: paragraphs.map((paragraph) => paragraph.id),
        addedIds: [],
        removedIds: [],
        usedFullParse: true,
        localizationFailed: false,
    }
}
